import React, {
  ReactNode,
  useState,
  FunctionComponent,
  useEffect,
  useContext,
  useRef,
} from "react";
import { Button, CloseIconThin } from "@cmsgov/ds-medicare-gov";
import classNames from "classnames";
import { useTranslate } from "../../helpers";
import { AppContext } from "../../app/store";
import { ActionType } from "../../@types";
import { AlertMessageConditions } from "../../app/contexts/AlertMessaging/types";
import {
  alertMessageConditionTopicMap,
  alertMessageTestId,
  alertMessageTitleTestId,
  alertMessageBodyTestId,
  alertMessageActionTestId,
} from "./constants";
import {
  AnalyticsActionType,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
  Ga4Event,
  Ga4EventType,
} from "../../app/contexts/Analytics/types";

export interface AlertMessageProps {
  action?: string | ReactNode;
  body: string | ReactNode;
  className?: string;
  condition?: AlertMessageConditions;
  dataTestId?: string;
  hideCloseButton?: boolean;
  icon?: ReactNode;
  narrowWidth?: boolean;
  onClose?: () => void;
  title?: string;
}

const AlertMessage: FunctionComponent<AlertMessageProps> = ({
  action,
  body,
  className = "",
  condition,
  dataTestId = "",
  hideCloseButton = false,
  icon,
  narrowWidth = false,
  onClose,
  title,
}) => {
  const t = useTranslate();
  const { state, dispatch } = useContext(AppContext);
  const [active, setActive] = useState(true);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const mctAlertTopic = condition && alertMessageConditionTopicMap[condition];

  const lcpAlertClosedAnalytics = () => {
    if (mctAlertTopic) {
      dispatch({
        type: AnalyticsActionType.SEND_GA4_EVENT,
        settings: {
          event_name: Ga4Event.LOWEST_COST_PHARMA_ALERT_CLOSED,
          event_type: Ga4EventType.UI_INTERACTION,
          mct_alert_topic: mctAlertTopic,
          heading: title,
          alert_type: "warn", // Variation is a guess. Docs suggest there should be an
          // "informative," "lightweight," and "status" option (which aren't
          // existing variations). We aren't using DS Alert, either
          text: bodyRef.current?.textContent || undefined,
        },
      });
      dispatch({
        type: AnalyticsActionType.SEND_TEALIUM_EVENT,
        settings: {
          event_action: "lowest cost pharmacy alert closed",
          event_label: `${mctAlertTopic}${title ? `: ${title}` : ""}`,
        },
      });
    }
  };

  const handleOnClose = () => {
    onClose && onClose();
    dispatch({
      type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
      settings: {
        button: {
          buttonStyle: AnalyticsButtonStyle.TRANSPARENT,
          buttonType: AnalyticsButtonType.BUTTON,
          text: t("close"),
        },
      },
    });
    lcpAlertClosedAnalytics();
    setActive(false);
  };

  useEffect(() => {
    if (!bodyRef.current || !condition) {
      return;
    }
    const trackedLcpAlertImpressions =
      state.trackedEvents[Ga4Event.LOWEST_COST_PHARMA_ALERT_IMPRESSION] || {};
    const needsUpdate = !trackedLcpAlertImpressions[condition];
    if (needsUpdate && mctAlertTopic) {
      dispatch({
        type: ActionType.EVENT_TRACKED,
        payload: {
          event: Ga4Event.LOWEST_COST_PHARMA_ALERT_IMPRESSION,
          value: { ...trackedLcpAlertImpressions, [condition]: true },
        },
      });
      dispatch({
        type: AnalyticsActionType.SEND_GA4_EVENT,
        settings: {
          event_name: Ga4Event.LOWEST_COST_PHARMA_ALERT_IMPRESSION,
          event_type: Ga4EventType.NON_INTERACTION,
          mct_alert_topic: mctAlertTopic,
          heading: title,
          alert_type: "warn", // Variation is a guess. Docs suggest there should be an
          // "informative," "lightweight," and "status" option (which aren't
          // existing variations). We aren't using DS Alert, either
          text: bodyRef.current.textContent || undefined,
        },
      });

      dispatch({
        type: AnalyticsActionType.SEND_TEALIUM_EVENT,
        settings: {
          event_action: "lowest cost pharmacy alert impression",
          event_label: `${mctAlertTopic}${title ? `: ${title}` : ""}`,
        },
      });
    }
  }, [bodyRef.current]);

  return (
    <div
      className={classNames("mct-c-alert-message", className, {
        ["mct-c-alert-message--hidden"]: !active,
        ["mct-c-alert-message--narrow"]: narrowWidth,
      })}
      data-testid={dataTestId}
      data-cy={alertMessageTestId}
    >
      {icon && <div className="mct-c-alert-message__icon">{icon}</div>}
      {title && (
        <h3
          className="mct-c-alert-message__title"
          data-cy={alertMessageTitleTestId}
        >
          {title}
        </h3>
      )}
      <div
        className={classNames("mct-c-alert-message__body", {
          "mct-c-alert-message__body--no-title": !title,
        })}
        ref={bodyRef}
        data-cy={alertMessageBodyTestId}
      >
        {body}
      </div>

      {!narrowWidth && (
        <div className="mct-c-alert-message__combined-content">
          {title && (
            <h3
              className="mct-c-alert-message__combined-title"
              data-cy={alertMessageTitleTestId}
            >
              {title}
            </h3>
          )}
          <div
            className={classNames("mct-c-alert-message__combined-body", {
              "mct-c-alert-message__combined-body--no-title": !title,
            })}
            data-cy={alertMessageBodyTestId}
          >
            {body}
          </div>
        </div>
      )}
      {action && (
        <div
          className="mct-c-alert-message__action"
          data-cy={alertMessageActionTestId}
        >
          {action}
        </div>
      )}
      {!hideCloseButton && (
        <div className="mct-c-alert-message__close-button">
          <Button
            variation="ghost"
            onClick={handleOnClose}
            aria-label={t("close")}
          >
            <CloseIconThin className="ds-u-font-size--xl ds-u-color--gray-light" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default AlertMessage;
